import { Link, useSearch } from '@tanstack/react-location'
import { Dropdown } from 'CRM/screens/Apartments/components/Dropdown'
import { useFormik } from 'formik'
import ActionSuccessModal from 'MediaManager/components/ActionSuccessModal'
import FileUpload from 'MediaManager/components/FileUpload'
import Input from 'MediaManager/components/Form/Input'
import { uploadFileToGCP, uploadFileToS3 } from 'MediaManager/helpers/s3'
import ScreenLayout from 'MediaManager/layouts/ScreenLayout'
import { getVendors } from 'MediaManager/services'
import { getModelById, upsertModel } from 'MediaManager/services/models'
import { getManageProperties } from 'MediaManager/services/property'
import React, { useCallback, useEffect, useMemo } from 'react'
import Button from 'shared/components/Button'
import Loader from 'shared/components/Loader'
import Select from 'shared/components/Select'
import { useRequest } from 'shared/hooks/useRequest'
import { errorMessages } from 'shared/settings'
import * as Yup from 'yup'

function Edit3DModelsScreen(props) {
  const { id } = useSearch()

  const [resultValue, setResultValue] = React.useState(null)

  const getModelRequest = useCallback(async () => {
    if (!id) {
      return null
    }
    const { data } = await getModelById(id)
    return data.models[0]
  }, [id])

  const { data: model, loading } = useRequest(getModelRequest)

  const getVendorsRequest = useCallback(async () => {
    const { data } = await getVendors()
    return data.vendors
  }, [])

  const { data: vendors, loading: vendorsLoading } = useRequest(getVendorsRequest)

  const vendorsOptions = useMemo(() => {
    if (vendors && !vendorsLoading) {
      return vendors.map((v) => ({
        label: v.full_name,
        value: v.full_name,
      }))
    }
  }, [vendors, vendorsLoading])

  const getPropertiesRequest = useCallback(async () => {
    const { data } = await getManageProperties()
    return data.properties
  }, [])

  const { data: properties, loading: propertiesLoading } = useRequest(getPropertiesRequest)

  const propertiesOptions = useMemo(() => {
    if (properties && !propertiesLoading) {
      return properties.map((v) => ({
        label: v.property_name,
        value: v.property_name,
      }))
    }
  }, [properties, propertiesLoading])

  useEffect(() => {
    if (model) {
      formik.setValues({
        high_res_model_path: model.high_res_model_path || '',
        high_res_polygons_count: model.high_res_polygons_count || '',
        high_res_size_in_bytes: model.high_res_size_in_bytes || '',
        low_res_model_path: model.low_res_model_path || '',
        low_res_polygons_count: model.low_res_polygons_count || '',
        low_res_size_in_bytes: model.low_res_size_in_bytes || '',
        bounding_box: model.bounding_box || '',
        initial_gravity_rotation_angle: model.initial_gravity_rotation_angle || 0,
        scale: model.scale || 1,
      })
    }
  }, model)

  const validationSchema = Yup.object().shape({
    // asset_id: Yup.string().required(errorMessages.required),
  })

  function generatePath(file) {
    const { values } = formik
    const filePathSuffix = `${file.name.indexOf('.glb') ? `glb/` : ''}${Date.now()}-${file.name.replace('.glb', '')}`
    if (formik.values.scope === 'vendor') {
      return `models/${values.vendor ? `${values.vendor.replaceAll(" ", "_")}/` : ''}${filePathSuffix}`
    }
    if (formik.values.scope === 'property') {
      return `properties/${formik.values.property.replaceAll(" ", "_")}/models/${filePathSuffix}`
    }
  }

  const formik = useFormik({
    initialValues: {
      scope: 'property',
    },
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema,
    // validateOnMount:true,
    onSubmit: async (values) => {
      try {
        console.log('Submitting values:', values)
        // Upload files to S3 or GCP
        let highResFilePath = values.high_res_model_path
        if (values.highResFile) {
          highResFilePath = await uploadFileToS3(
            values.highResFile,
            generatePath(values.highResFile),
            'sparx-secured-cdn',
          )
        }
        // Upload files to S3 or GCP
        let lowResFilePath = values.low_res_model_path
        if (values.lowResFile) {
          lowResFilePath = await uploadFileToS3(values.lowResFile, generatePath(values.lowResFile), 'sparx-secured-cdn')
        }

        // Extract file extension from highResFile if present
        let highResFileExtension = ''
        if (values.highResFile && values.highResFile.name) {
          const parts = values.highResFile.name.split('.')
          if (parts.length > 1) {
            highResFileExtension = parts.pop().toLowerCase()
          }
        }

        await upsertModel({
          id: id ? parseInt(id) : undefined,
          high_res_model_path: highResFilePath,
          high_res_polygons_count: values.high_res_polygons_count || 0,
          high_res_size_in_bytes: values.high_res_size || 0,
          low_res_model_path: lowResFilePath,
          low_res_polygons_count: values.low_res_polygons_count || 0,
          low_res_size_in_bytes: values.low_res_size || 0,
          bounding_box: values.bounding_box || '',
          model_type: highResFileExtension,
          initial_gravity_rotation_angle: values.initial_gravity_rotation_angle || 0,
          scale: values.scale || 1,
        })

        setResultValue('success')
      } catch (error) {
        console.log(error)
        setResultValue('error')
      }
    },
  })

  function isValid() {
    if (!formik.values.scope) {
      return false
    }
    if (!formik.values.high_res_model_path && !formik.values.highResFile) {
      return false
    }
    if (formik.values.scope === 'vendor' && !formik.values.vendor) {
      return false
    }
    if (formik.values.scope === 'property' && !formik.values.property) {
      return false
    }
    return true
  }

  useEffect(() => {
    if (
      vendorsOptions &&
      vendorsOptions.length > 0 &&
      !formik.values.vendor &&
      model &&
      propertiesOptions &&
      propertiesOptions.length > 0
    ) {
      const path = model.high_res_model_path || model.low_res_model_path || ''
      const [vendor] = vendorsOptions.filter((v) => path.toLowerCase().includes(v.value.replaceAll(" ", "_").toLowerCase()))
      const [property] = propertiesOptions.filter((v) => path.toLowerCase().includes(v.value.replaceAll(" ", "_").toLowerCase()))

      formik.setFieldValue('vendor', vendor ? vendor.value : vendorsOptions[0].value)
      const scope = path.indexOf('properties') > -1 ? 'property' : !!vendor ? 'vendor' : ''
      formik.setFieldValue('scope', scope)
      if (id && scope) {
        formik.setFieldValue('editScope', scope)
      }
      formik.setFieldValue('property', property ? property.value : propertiesOptions[0].value)
    }
  }, [vendorsOptions, model, propertiesOptions, id])

  const isLoading = loading || vendorsLoading || propertiesLoading

  return (
    <ScreenLayout title={`${id ? 'Edit' : 'Add'} 3D Models`}>
      {resultValue && (
        <ActionSuccessModal
          state={resultValue}
          action="Model"
          onClose={() => {
            setResultValue(null)
          }}
          skipConfirm
        />
      )}
      {!isLoading && (
        <form onSubmit={formik.handleSubmit}>
          <div className="flex items-center gap-[16px] mb-[16px]">
            <FileUpload
              label="High resolution model file"
              name="highResFile"
              onChange={(file) => {
                formik.setFieldValue('highResFile', file)
                formik.setFieldValue('high_res_model_path', file ? '' : model.high_res_model_path)
              }}
            />
            <FileUpload
              label="Low resolution model file"
              name="lowResFile"
              onChange={(file) => {
                formik.setFieldValue('lowResFile', file)
                formik.setFieldValue('low_res_model_path', file ? '' : model.low_res_model_path)
              }}
            />
          </div>
          <div>
            <div className="mb-[16px] text-[14px] opacity-50">Model scope</div>
            <div className="flex items-center justify-between">
              {(!formik.values.editScope || formik.values.editScope === 'property') && (
                <Button
                  onClick={() => {
                    formik.setFieldValue('scope', 'property')
                  }}
                  className={`w-full ${formik.values.scope === 'property' ? '' : 'opacity-30'}`}
                  rounded
                >
                  Property
                </Button>
              )}
              {(!formik.values.editScope || formik.values.editScope === 'vendor') && (
                <Button
                  onClick={() => {
                    formik.setFieldValue('scope', 'vendor')
                  }}
                  className={`w-full ml-[16px] ${formik.values.scope === 'vendor' ? '' : 'opacity-30'}`}
                  rounded
                >
                  Vendor
                </Button>
              )}
            </div>
            {id && formik.values.scope && (
              <div className="mt-[16px] opacity-50">
                <small>
                  Scope is locked to already uploaded / used scope for this model, which is : {formik.values.scope}
                </small>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 gap-[16px] mb-[16px] mt-[32px]">
            {
              <div className={`${id && formik.values.editScope ? 'opacity-50 pointer-events-none' : ''}`}>
                {!vendorsLoading && formik.values.scope === 'vendor' && (
                  <div>
                    <Select
                      value={vendorsOptions.find((v) => v.value === formik.values.vendor) || vendorsOptions[0]}
                      label="Vendor"
                      // placeholder="Bedrooms #"
                      onChange={(val) => {
                        formik.setFieldValue('vendor', val.value)
                      }}
                      options={vendorsOptions}
                    />
                    <small className="opacity-20">
                      This field is auto populated from path of the model, if path does not contain vendor name or that
                      could't be parsed, value will be empty.
                    </small>
                  </div>
                )}
                {!propertiesLoading && formik.values.scope === 'property' && (
                  <div>
                    <Select
                      value={propertiesOptions.find((v) => v.value === formik.values.property) || propertiesOptions[0]}
                      label="Property"
                      // placeholder="Bedrooms #"
                      onChange={(val) => {
                        formik.setFieldValue('property', val.value)
                      }}
                      options={propertiesOptions}
                    />
                    <small className="opacity-20">
                      This field is auto populated from path of the model, if path does not contain property name or
                      that could't be parsed, value will be empty.
                    </small>
                  </div>
                )}
              </div>
            }
            <Input
              label="High resolution file path"
              name="high_res_model_path"
              value={formik.values.high_res_model_path || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.high_res_model_path && formik.errors.high_res_model_path}
              required
              disabled
              placeholder="This field is auto-filled after file upload"
            />
            <Input
              label="High resolution polygons count"
              name="high_res_polygons_count"
              type="number"
              value={formik.values.high_res_polygons_count || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.high_res_polygons_count && formik.errors.high_res_polygons_count}
            />
            <Input
              label="High resolution size in bytes"
              name="high_res_size_in_bytes"
              type="number"
              value={formik.values.high_res_size_in_bytes || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.high_res_size_in_bytes && formik.errors.high_res_size_in_bytes}
            />
            <Input
              label="Low resolution file path"
              name="low_res_model_path"
              value={formik.values.low_res_model_path || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.low_res_model_path && formik.errors.low_res_model_path}
              disabled
              placeholder="This field is auto-filled after file upload"
            />
            <Input
              label="Low resolution polygons count"
              name="low_res_polygons_count"
              type="number"
              value={formik.values.low_res_polygons_count || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.low_res_polygons_count && formik.errors.low_res_polygons_count}
            />
            <Input
              label="Low resolution size in bytes"
              name="low_res_size_in_bytes"
              type="number"
              value={formik.values.low_res_size_in_bytes || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.low_res_size_in_bytes && formik.errors.low_res_size_in_bytes}
            />
            <div className="grid grid-cols-3 gap-[16px]">
              <Input
                label="Bounding Box"
                name="bounding_box"
                value={formik.values.bounding_box || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.bounding_box && formik.errors.bounding_box}
              />
              <Input
                label="Initial gravity rotate angle"
                name="initial_gravity_rotation_angle"
                type="number"
                value={formik.values.initial_gravity_rotation_angle || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.initial_gravity_rotation_angle && formik.errors.initial_gravity_rotation_angle}
              />
              <Input
                label="Scale"
                name="scale"
                type="number"
                value={formik.values.scale || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.scale && formik.errors.scale}
              />
            </div>
          </div>
          <div className="flex items-center justify-end w-full">
            <Link to="/models/manage">
              <Button type="submit" variant="secondary" rounded>
                Cancel
              </Button>
            </Link>
            <Button type="submit" rounded className="ml-[16px]" disabled={!isValid()} loading={formik.isSubmitting}>
              {id ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      )}
      {isLoading && (
        <div className="flex items-center justify-center">
          <Loader />
        </div>
      )}
    </ScreenLayout>
  )
}

export default Edit3DModelsScreen
